import React, { createContext, useState, useEffect, useContext } from 'react';
import { notificationService } from '../services/api';
import AuthContext from './AuthContext';

const NotificationContext = createContext();

export const NotificationProvider = ({ children }) => {
    const [notifications, setNotifications] = useState([]);
    const [unreadCount, setUnreadCount] = useState(0);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const { isAuthenticated } = useContext(AuthContext);

    // Load notifications on mount and when auth state changes
    useEffect(() => {
        if (isAuthenticated) {
            fetchNotifications();
        } else {
            setNotifications([]);
            setUnreadCount(0);
            setLoading(false);
        }
    }, [isAuthenticated]);

    // Fetch notifications from API
    const fetchNotifications = async () => {
        try {
            setLoading(true);
            const response = await notificationService.getNotifications();
            setNotifications(response.data.data);
            setUnreadCount(response.data.unreadCount);
            setLoading(false);
        } catch (err) {
            console.error('Error fetching notifications:', err);
            setError('Failed to load notifications');
            setLoading(false);
        }
    };

    // Mark a notification as read
    const markAsRead = async (id) => {
        try {
            const response = await notificationService.markAsRead(id);

            // Update the notifications state
            setNotifications(prevNotifications =>
                prevNotifications.map(notification =>
                    notification._id === id ? { ...notification, read: true } : notification
                )
            );

            // Update unread count
            setUnreadCount(prevCount => (prevCount > 0 ? prevCount - 1 : 0));

            return response.data;
        } catch (err) {
            console.error('Error marking notification as read:', err);
            setError('Failed to mark notification as read');
            throw err;
        }
    };

    // Mark all notifications as read
    const markAllAsRead = async () => {
        try {
            await notificationService.markAllAsRead();

            // Update all notifications to read
            setNotifications(prevNotifications =>
                prevNotifications.map(notification => ({ ...notification, read: true }))
            );

            // Reset unread count
            setUnreadCount(0);
        } catch (err) {
            console.error('Error marking all notifications as read:', err);
            setError('Failed to mark all notifications as read');
            throw err;
        }
    };

    // Delete a notification
    const deleteNotification = async (id) => {
        try {
            await notificationService.deleteNotification(id);

            // Remove from state
            const updatedNotifications = notifications.filter(notification => notification._id !== id);
            setNotifications(updatedNotifications);

            // Update unread count if necessary
            const deletedNotification = notifications.find(notification => notification._id === id);
            if (deletedNotification && !deletedNotification.read) {
                setUnreadCount(prevCount => (prevCount > 0 ? prevCount - 1 : 0));
            }
        } catch (err) {
            console.error('Error deleting notification:', err);
            setError('Failed to delete notification');
            throw err;
        }
    };

    // Refresh notifications (manual refresh)
    const refreshNotifications = () => {
        fetchNotifications();
    };

    return (
        <NotificationContext.Provider
            value={{
                notifications,
                unreadCount,
                loading,
                error,
                markAsRead,
                markAllAsRead,
                deleteNotification,
                refreshNotifications
            }}
        >
            {children}
        </NotificationContext.Provider>
    );
};

export default NotificationContext; 